import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { AuthState, AuthActions, LoginRequest } from '../types/auth.types';
import { authService } from '../services/authService';
import { getAuthErrorMessage } from '../utils/authErrorMessages';
import { logger } from '@/shared/lib/logger';
import { HTTP_STATUS } from '@/shared/constants/httpStatus';

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  isLoading: false,
  isInitialized: false,
  error: null,

  // 2FA初期状態
  pendingTwoFactor: false,
  maskedEmail: null,
  twoFactorExpiresAt: null,
  debugCode: null,
};

// checkAuthの連続実行を防ぐフラグ
let isCheckingAuth = false;

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set) => ({
      ...initialState,

      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.login(credentials);

          // 2FA必要な場合（pendingTokenはHttpOnly Cookieとしてブラウザが自動管理）
          if (response.requiresTwoFactor === true) {
            set({
              pendingTwoFactor: true,
              maskedEmail: response.maskedEmail ?? null,
              twoFactorExpiresAt: response.expiresAt ?? null,
              debugCode: response.debugCode ?? null,
              isLoading: false,
            });
            return;
          }

          // 通常ログイン成功
          set({
            isAuthenticated: true,
            user: response.user ?? null,
            pendingTwoFactor: false,
            maskedEmail: null,
            twoFactorExpiresAt: null,
            debugCode: null,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = getAuthErrorMessage({
            error,
            fallbackMessage: '認証に失敗しました',
          });
          set({
            isAuthenticated: false,
            user: null,
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authService.logout();
        } catch (error) {
          logger.captureError(error, 'authStore.logout');
        } finally {
          // Cookie方式: Cookieはサーバー側で削除される
          // isInitializedはtrueのまま維持
          set({
            ...initialState,
            isInitialized: true,
          });
        }
      },

      checkAuth: async () => {
        // 連続実行を防ぐガード
        if (isCheckingAuth) {
          return;
        }

        isCheckingAuth = true;

        try {
          // Cookie方式: Cookieの確認は不要（サーバー側で検証）
          set({ isLoading: true });
          try {
            const user = await authService.getSession();
            set({
              isAuthenticated: true,
              user,
              isLoading: false,
              isInitialized: true,
              error: null,
            });
          } catch {
            // セッションが無効な場合は未認証状態に設定
            set({
              isAuthenticated: false,
              user: null,
              isLoading: false,
              isInitialized: true,
              error: null,
            });
          }
        } finally {
          isCheckingAuth = false;
        }
      },

      clearError: () => {
        set({ error: null });
      },

      verifyTwoFactor: async (code: string) => {
        set({ isLoading: true, error: null });

        try {
          // pendingTokenはHttpOnly Cookieから自動送信される
          const response = await authService.verifyTwoFactor({ code });

          set({
            isAuthenticated: true,
            user: response.user,
            pendingTwoFactor: false,
            maskedEmail: null,
            twoFactorExpiresAt: null,
            debugCode: null,
            isLoading: false,
          });
        } catch (error) {
          const errorMessage = getAuthErrorMessage({
            error,
            fallbackMessage: '認証コードの検証に失敗しました',
          });

          // HTTP 429（試行回数超過）または HTTP 400（期限切れ）の場合は2FAフローを終了
          // サーバー側でpendingToken Cookieは削除済み
          const axiosError = error as { response?: { status?: number } };
          const statusCode = axiosError.response?.status;

          if (statusCode === HTTP_STATUS.TOO_MANY_REQUESTS || statusCode === HTTP_STATUS.BAD_REQUEST) {
            set({
              pendingTwoFactor: false,
              maskedEmail: null,
              twoFactorExpiresAt: null,
              debugCode: null,
              isLoading: false,
              error: errorMessage,
            });
          } else {
            // HTTP 401（コード不一致）は再試行可能なのでpendingTwoFactorは維持
            set({ error: errorMessage, isLoading: false });
          }

          throw error;
        }
      },

      resendTwoFactorCode: async () => {
        set({ isLoading: true, error: null });

        try {
          // pendingTokenはHttpOnly Cookieから自動送信される
          const response = await authService.resendTwoFactorCode();

          set({
            maskedEmail: response.maskedEmail,
            twoFactorExpiresAt: response.expiresAt,
            // 再送信でサーバー側のコードは新しくなり、以前のコードは無効になる。
            // 応答に無い場合も null にして、無効になったコードを表示し続けない。
            debugCode: response.debugCode ?? null,
            isLoading: false,
          });
        } catch (error) {
          const errorMessage = getAuthErrorMessage({
            error,
            fallbackMessage: 'コードの再送信に失敗しました',
          });
          set({ error: errorMessage, isLoading: false });
          throw error;
        }
      },

      cancelTwoFactor: () => {
        // pendingToken Cookieの削除について:
        // - pendingTokenはMax-Age=600（10分）で自動期限切れとなる
        // - path=/api/auth に限定されているため他APIへの影響なし
        set({
          pendingTwoFactor: false,
          maskedEmail: null,
          twoFactorExpiresAt: null,
          debugCode: null,
          error: null,
        });
      },
    }),
    {
      name: 'auth-store',
    }
  )
);